import { Badge, Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import {
  IntegrationService,
  useDataConnectorsQuery,
  useDataConnectorJobsQuery,
  zIntegrationService,
} from 'src/api/queries';
import {
  useUpdateCampaignFilterMutation,
  useUpdateSelectedAccountsMutation,
} from 'src/api/mutations';
import { DataConnector } from 'src/components/organisms/DataConnector';
import { API_BASE_URL } from 'src/constants';
import { useAppContext } from 'src/contexts/AppContext';
import { z } from 'zod';
import { Icon } from 'src/components/atoms/Icon';
import { ProgressSlider } from 'src/components/atoms/ProgressSlider';
import { CustomToast } from 'src/components/atoms/CustomToast/index';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { SelectAccountsModal } from 'src/features/Settings/components/molecules/SelectAccountsModal';
import { CampaignFilterModal } from 'src/features/Settings/components/molecules/CampaignFilterModal';
import {
  SERVICES_REQUIRING_ACCOUNT_SELECTION,
  SERVICES_SUPPORTING_CAMPAIGN_FILTER,
} from 'src/features/Settings/utils/constants';

// Define connector sections organized by category
const connectorSections = [
  {
    category: 'Media Channels',
    sections: [
      {
        title: 'Facebook',
        connectors: [
          {
            name: 'Facebook ADS',
            icon: <Icon iconName="facebook" boxSize={6} />,
            service: 'facebook' as IntegrationService,
          },
        ],
      },
      {
        title: 'Instagram',
        connectors: [
          {
            name: 'Instagram ADS',
            icon: <Icon iconName="instagram" boxSize={6} />,
            service: 'instagram' as IntegrationService,
          },
        ],
      },
      {
        title: 'Google ADS',
        connectors: [
          {
            name: 'Google ADS',
            icon: <Icon iconName="google-ads" boxSize={6} />,
            service: 'google_ads' as IntegrationService,
          },
        ],
      },
      {
        title: 'YouTube',
        connectors: [
          {
            name: 'YouTube ADS',
            icon: <Icon iconName="youtube" boxSize={6} />,
            service: 'youtube' as IntegrationService,
          },
        ],
      },
      {
        title: 'LinkedIn Ads',
        connectors: [
          {
            name: 'LinkedIn Ads',
            icon: <Icon iconName="linkedin" boxSize={6} />,
            service: 'linkedin' as IntegrationService,
          },
        ],
      },
      {
        title: 'Display & Video 360 (DV360)',
        connectors: [
          {
            name: 'Display & Video 360',
            icon: <Icon iconName="dv360" boxSize={6} />,
            service: 'dv360' as IntegrationService,
          },
        ],
      },
      {
        title: 'Pinterest',
        connectors: [
          {
            name: 'Pinterest',
            icon: <Icon iconName="pinterest" boxSize={6} />,
            service: 'pinterest' as IntegrationService,
          },
        ],
      },
      {
        title: 'TikTok',
        connectors: [
          {
            name: 'TikTok',
            icon: <Icon iconName="tiktok" boxSize={6} />,
            service: 'tiktok' as IntegrationService,
          },
        ],
      },
      {
        title: 'Snapchat',
        connectors: [
          {
            name: 'Snapchat',
            icon: <Icon iconName="snapchat" boxSize={6} />,
            service: 'snapchat' as IntegrationService,
          },
        ],
      },
      {
        title: 'Campaign Manager 360 (CM360)',
        connectors: [
          {
            name: 'Campaign Manager 360',
            icon: <Icon iconName="cv360" boxSize={6} />,
            service: 'cm360' as IntegrationService,
          },
        ],
      },
      {
        title: 'Adform',
        connectors: [
          {
            name: 'Adform',
            icon: <Icon iconName="adform" boxSize={6} />,
            service: 'adform' as IntegrationService,
          },
        ],
      },
    ],
  },
  {
    category: 'KPI Sources',
    sections: [
      {
        title: 'Google Analytics 4',
        connectors: [
          {
            name: 'Google Analytics 4',
            icon: <Icon iconName="google-analytics" boxSize={6} />,
            service: 'ga4' as IntegrationService,
          },
        ],
      },
      {
        title: 'Shopify',
        connectors: [
          {
            name: 'Shopify',
            icon: <Icon iconName="shopify" boxSize={6} />,
            service: 'shopify' as IntegrationService,
          },
        ],
      },
    ],
  },
];

const zDataConnectionMessage = z.object({
  ok: z.boolean(),
  integration: zIntegrationService.optional(),
  customer: z.string().optional(),
  messageType: z.literal('oauth-callback'),
});

type DataConnectorsProps = {
  showFetchStatus?: boolean;
};

export const DataConnectors: React.FC<DataConnectorsProps> = ({
  showFetchStatus = false,
}) => {
  const { currentCustomer } = useAppContext();
  const previousConnectedConnectors = useRef<string[]>([]);
  const { toastState, hideToast, showSuccess, showError } = useCustomToast();

  // Fetch available connectors (just for checking available connectors)
  // Note: Currently not used as connector list is hardcoded in connectorSections
  const {
    data: connectorsData,
    refetch,
    isLoading,
  } = useDataConnectorsQuery(
    { customer: currentCustomer?.customerName ?? '' },
    !!currentCustomer?.customerName,
  );

  // Fetch data connector jobs to determine connected status
  // Always fetch to get connected status, but use different polling intervals
  const { data: jobsData, refetch: refetchJobs } = useDataConnectorJobsQuery(
    { customer: currentCustomer?.customerName ?? '', job_type: 'initial' },
    !!currentCustomer?.customerName,
    showFetchStatus ? 5000 : false, // Poll every 5 seconds only when showing fetch status
  );

  const { mutate: updateSelectedAccounts, isLoading: isSavingAccounts } =
    useUpdateSelectedAccountsMutation();

  const [isSelectAccountsOpen, setIsSelectAccountsOpen] = useState(false);
  const [serviceForAccountSelection, setServiceForAccountSelection] =
    useState<IntegrationService | null>(null);

  const handleSaveSelectedAccounts = useCallback(
    (selectedAccountIds: string[]) => {
      if (!currentCustomer?.customerName || !serviceForAccountSelection) return;

      updateSelectedAccounts(
        {
          customer: currentCustomer.customerName,
          service: serviceForAccountSelection,
          selectedAccountIds,
        },
        {
          onSuccess: () => {
            const connectorInfo = connectorSections
              .flatMap((category) => category.sections)
              .flatMap((section) => section.connectors)
              .find((c) => c.service === serviceForAccountSelection);

            showSuccess(
              `${connectorInfo?.name ?? serviceForAccountSelection} accounts saved`,
              'Data will now sync for selected accounts.',
            );
            setIsSelectAccountsOpen(false);
            setServiceForAccountSelection(null);
            refetch();
            refetchJobs();
          },
          onError: (error) => {
            const defaultMessage = 'Please try again or contact support.';
            const apiMessage = (
              error as { response?: { data?: { message?: string } } }
            )?.response?.data?.message;
            const errorMessage =
              !apiMessage || apiMessage === 'Internal server error.'
                ? defaultMessage
                : apiMessage;
            showError('Failed to save account selection', errorMessage);
          },
        },
      );
    },
    [
      currentCustomer?.customerName,
      serviceForAccountSelection,
      updateSelectedAccounts,
      refetch,
      refetchJobs,
      showSuccess,
      showError,
    ],
  );

  const connectorsByService = useMemo(() => {
    return new Map(
      (connectorsData ?? []).map((connector) => [connector.service, connector]),
    );
  }, [connectorsData]);

  const { mutate: updateCampaignFilter, isLoading: isSavingCampaignFilter } =
    useUpdateCampaignFilterMutation();

  const [isCampaignFilterOpen, setIsCampaignFilterOpen] = useState(false);
  const [serviceForCampaignFilter, setServiceForCampaignFilter] =
    useState<IntegrationService | null>(null);

  const handleSaveCampaignFilter = useCallback(
    (campaignNameFilter: string | null) => {
      if (!currentCustomer?.customerName || !serviceForCampaignFilter) return;

      updateCampaignFilter(
        {
          customer: currentCustomer.customerName,
          service: serviceForCampaignFilter,
          campaignNameFilter,
        },
        {
          onSuccess: () => {
            showSuccess(
              'Campaign filter saved',
              'Matching campaigns are being imported.',
            );
            setIsCampaignFilterOpen(false);
            setServiceForCampaignFilter(null);
            refetch();
            refetchJobs();
          },
          onError: (error) => {
            const defaultMessage = 'Please try again or contact support.';
            const apiMessage = (
              error as { response?: { data?: { message?: string } } }
            )?.response?.data?.message;
            const errorMessage =
              !apiMessage || apiMessage === 'Internal server error.'
                ? defaultMessage
                : apiMessage;
            showError('Failed to save campaign filter', errorMessage);
          },
        },
      );
    },
    [
      currentCustomer?.customerName,
      serviceForCampaignFilter,
      updateCampaignFilter,
      refetch,
      refetchJobs,
      showSuccess,
      showError,
    ],
  );

  // Create a map of service to job status for quick lookup
  const jobStatusMap = useMemo(() => {
    if (!jobsData) return new Map();
    return new Map(jobsData.map((job) => [job.service, job]));
  }, [jobsData]);

  // Determine connected services from jobs data
  // A service is connected if it has a job entry
  const connectedServices = useMemo(() => {
    if (!jobsData) return [] as string[];
    return jobsData.map((job) => job.service);
  }, [jobsData]);

  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      // Require the event come from accepted origin
      if (event.origin !== API_BASE_URL) {
        return;
      }

      const { data, error } = zDataConnectionMessage.safeParse(event.data);

      if (error) {
        return;
      }

      // eslint-disable-next-line no-console
      console.log('Data connection message', data);

      // Show error toast for failed OAuth connection
      if (!data.ok) {
        // eslint-disable-next-line no-console
        console.error('Oauth connection failed');

        // Find the display name for this service
        const connectorInfo = connectorSections
          .flatMap((category) => category.sections)
          .flatMap((section) => section.connectors)
          .find((connector) => connector.service === data.integration);

        const displayName =
          connectorInfo?.name || data.integration || 'Unknown service';

        showError(
          `${displayName} connection failed`,
          'Please try connecting again or contact support if the issue persists',
        );
      }

      if (data.customer !== currentCustomer?.customerName) {
        return;
      }

      // Refetch both connectors and jobs to update connected status
      refetch();
      refetchJobs();

      if (
        data.ok &&
        data.integration &&
        SERVICES_REQUIRING_ACCOUNT_SELECTION.includes(data.integration)
      ) {
        setServiceForAccountSelection(data.integration);
        setIsSelectAccountsOpen(true);
      }
    };

    window.addEventListener('message', messageHandler);

    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, [currentCustomer, refetch, refetchJobs, showError]);

  // Effect to detect new connections and show toast
  useEffect(() => {
    if (!jobsData) return;

    const currentConnectedServices = connectedServices;
    const previousConnectedServices = previousConnectedConnectors.current;

    // Initialize previous connected services on first load to prevent showing toast for existing connections
    if (previousConnectedServices.length === 0) {
      previousConnectedConnectors.current = currentConnectedServices;
      return;
    }

    // Find newly connected services
    const newConnections = currentConnectedServices.filter(
      (service) => !previousConnectedServices.includes(service),
    );

    // Show custom toast for each new connection
    newConnections.forEach((service) => {
      // Find the display name for this service
      const connectorInfo = connectorSections
        .flatMap((category) => category.sections)
        .flatMap((section) => section.connectors)
        .find((connector) => connector.service === service);

      const displayName = connectorInfo?.name || service;

      showSuccess(
        `${displayName} connected successfully`,
        'Data will now sync automatically',
        5000,
      );
    });

    // Update the previous connected connectors
    previousConnectedConnectors.current = currentConnectedServices;
  }, [connectedServices, jobsData, showSuccess]);

  // Calculate total connectors and connected connectors
  const totalConnectors = connectorSections.reduce(
    (total, categorySection) =>
      total +
      categorySection.sections.reduce(
        (sectionTotal, section) => sectionTotal + section.connectors.length,
        0,
      ),
    0,
  );

  const connectedConnectors = connectedServices.length;

  return (
    <Box w="full">
      {/* Custom Toast */}
      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        duration={toastState.duration}
        onClose={hideToast}
        position={{ top: '112px', right: '128px' }}
      />

      {/* Progress Slider */}
      <Box mb={8}>
        <ProgressSlider
          connected={connectedConnectors}
          total={totalConnectors}
        />
      </Box>

      {connectorSections.map((categorySection) => (
        <Box key={categorySection.category} mb={8}>
          <Text
            fontFamily="marketingPage"
            fontWeight="semibold"
            mb={7}
            fontSize="20px"
            color="text.title">
            {categorySection.category}
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingY={6} spacingX={6}>
            {categorySection.sections.map((section) => {
              const sectionConnector = section.connectors[0];
              const sectionConnectorData = sectionConnector
                ? connectorsByService.get(sectionConnector.service)
                : undefined;
              const showAccountWarning =
                sectionConnectorData?.status === 'success' &&
                sectionConnectorData?.selectedAccounts === null &&
                sectionConnector &&
                SERVICES_REQUIRING_ACCOUNT_SELECTION.includes(
                  sectionConnector.service,
                );
              const showCampaignFilterBadge =
                sectionConnectorData?.status === 'success' &&
                sectionConnector &&
                SERVICES_SUPPORTING_CAMPAIGN_FILTER.includes(
                  sectionConnector.service,
                );
              const activeCampaignFilter =
                sectionConnectorData?.campaignNameFilter ?? null;

              return (
                <Box key={section.title}>
                  <Flex
                    justify="space-between"
                    align="center"
                    mb={3}
                    minHeight="18px">
                    <Text
                      fontFamily="marketingPage"
                      fontWeight="medium"
                      fontSize="sm"
                      color="text.darkGray">
                      {section.title}
                    </Text>
                    <Flex align="center" gap={2}>
                      {showCampaignFilterBadge && (
                        <Badge
                          fontSize="xs"
                          bg="gray.100"
                          color="gray.700"
                          borderRadius="full"
                          textTransform="none"
                          px={3}
                          fontWeight="normal"
                          whiteSpace="nowrap"
                          cursor="pointer"
                          maxW="180px"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          onClick={() => {
                            setServiceForCampaignFilter(
                              sectionConnector.service,
                            );
                            setIsCampaignFilterOpen(true);
                          }}>
                          {activeCampaignFilter
                            ? `Filter: ${activeCampaignFilter}`
                            : 'Set campaign filter'}
                        </Badge>
                      )}
                      {showAccountWarning && (
                        <Badge
                          fontSize="xs"
                          bg="orange.100"
                          color="orange.700"
                          borderRadius="full"
                          textTransform="none"
                          px={3}
                          fontWeight="normal"
                          whiteSpace="nowrap"
                          cursor="pointer"
                          onClick={() => {
                            setServiceForAccountSelection(
                              sectionConnector.service,
                            );
                            setIsSelectAccountsOpen(true);
                          }}>
                          No accounts selected
                        </Badge>
                      )}
                    </Flex>
                  </Flex>
                  {section.connectors.map((connector) => {
                    // Determine connected status from jobs data
                    const integrationConnected = connectedServices.includes(
                      connector.service,
                    );
                    const jobStatus = jobStatusMap.get(connector.service);

                    return (
                      <DataConnector
                        connected={integrationConnected}
                        key={connector.service}
                        integration={connector.service}
                        isLoading={isLoading}
                        displayName={connector.name}
                        icon={connector.icon}
                        onError={showError}
                        showFetchStatus={showFetchStatus}
                        jobStatus={jobStatus}
                      />
                    );
                  })}
                </Box>
              );
            })}
          </SimpleGrid>
        </Box>
      ))}

      {serviceForAccountSelection && (
        <SelectAccountsModal
          isOpen={isSelectAccountsOpen}
          onClose={() => setIsSelectAccountsOpen(false)}
          onSave={handleSaveSelectedAccounts}
          service={serviceForAccountSelection}
          customer={currentCustomer?.customerName ?? ''}
          isSaving={isSavingAccounts}
          onError={showError}
        />
      )}

      {serviceForCampaignFilter && (
        <CampaignFilterModal
          isOpen={isCampaignFilterOpen}
          onClose={() => setIsCampaignFilterOpen(false)}
          onSave={handleSaveCampaignFilter}
          currentFilter={
            connectorsByService.get(serviceForCampaignFilter)
              ?.campaignNameFilter ?? null
          }
          isSaving={isSavingCampaignFilter}
          customer={currentCustomer?.customerName ?? ''}
        />
      )}
    </Box>
  );
};
